import React, { useState, useEffect } from 'react';
import './settings.css';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../ThemeContext';
import { useLogout } from '../../hooks/useLogout';
import { useAuthContext } from '../../hooks/useAuthContext';
import { apiFetch } from '../../api/client';
import { FiZap, FiBookmark, FiLogOut, FiTrash2, FiMoon, FiSun, FiArrowUpRight } from 'react-icons/fi';

function Settings() {
  const navigate = useNavigate();
  const { darkMode, setDarkMode } = useTheme();
  const { logout } = useLogout();
  const { user } = useAuthContext();

  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [managingPlan, setManagingPlan] = useState(false);

  useEffect(() => {
    apiFetch('/api/resume/usage').then(setUsage).catch((e) => setError(e.message));
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await apiFetch('/api/user/delete', { method: 'DELETE' });
      logout();
      navigate('/');
    } catch (e) {
      setError(e.message);
      setDeleting(false);
    }
  };

  const handleManagePlan = async () => {
    if (!usage || usage.tier === 'free') {
      navigate('/pricing');
      return;
    }
    setManagingPlan(true);
    setError(null);
    try {
      const data = await apiFetch('/api/billing/portal-session', { method: 'POST' });
      window.location.assign(data.url);
    } catch (e) {
      setError(e.message);
      setManagingPlan(false);
    }
  };

  const tierLabel = usage ? usage.limits.label : '—';

  return (
    <div className={`settingsContainer ${darkMode ? 'dark-mode' : 'light-mode'}`}>
      <div className="settingsInner">
        <h1 className="settingsTitle">Settings</h1>

        {error && <div className="settingsError">{error}</div>}

        {/* ===== Account ===== */}
        <div className="settingsCard">
          <h2 className="settingsCardTitle">Account</h2>
          <div className="settingsRow">
            <div>
              <span className="settingsLabel">Email</span>
              <span className="settingsValue">{user?.email || '—'}</span>
            </div>
          </div>
        </div>

        {/* ===== Subscription ===== */}
        <div className="settingsCard">
          <div className="settingsCardHead">
            <h2 className="settingsCardTitle">Subscription</h2>
            <span className={`tierBadge tier-${usage ? usage.tier : 'free'}`}>{tierLabel}</span>
          </div>

          {usage && (
            <div className="usageBlocks">
              <div className="usageBlock">
                <div className="usageBlockHead">
                  <span className="usageBlockLabel"><FiZap size={13} /> Generations today</span>
                  <span className="usageBlockCount">{usage.generationsToday} / {usage.limits.generationsPerDay}</span>
                </div>
                <div className="usageBar">
                  <div
                    className="usageBarFill"
                    style={{ width: `${Math.min(100, (usage.generationsToday / usage.limits.generationsPerDay) * 100)}%` }}
                  />
                </div>
              </div>

              <div className="usageBlock">
                <div className="usageBlockHead">
                  <span className="usageBlockLabel"><FiBookmark size={13} /> Saved resumes</span>
                  <span className="usageBlockCount">
                    {usage.savedCount} / {usage.limits.savedResumes}
                  </span>
                </div>
                <div className="usageBar">
                  <div
                    className="usageBarFill"
                    style={{
                      width: usage.limits.savedResumes === 0
                        ? '0%'
                        : `${Math.min(100, (usage.savedCount / usage.limits.savedResumes) * 100)}%`
                    }}
                  />
                </div>
                {usage.limits.savedResumes === 0 && (
                  <span className="usageNote">Saving requires a paid plan</span>
                )}
              </div>
            </div>
          )}

          <button className="setPillBtn solid" onClick={handleManagePlan} disabled={managingPlan}>
            <FiArrowUpRight size={14} />
            {managingPlan ? 'Redirecting…' : usage && usage.tier !== 'free' ? 'Manage plan' : 'Upgrade plan'}
          </button>
        </div>

        {/* ===== Appearance ===== */}
        <div className="settingsCard">
          <h2 className="settingsCardTitle">Appearance</h2>
          <div className="settingsRow between">
            <div>
              <span className="settingsLabel">Theme</span>
              <span className="settingsValue">{darkMode ? 'Dark' : 'Light'}</span>
            </div>
            <button className="setPillBtn ghost" onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? <><FiSun size={14} /> Light mode</> : <><FiMoon size={14} /> Dark mode</>}
            </button>
          </div>
        </div>

        {/* ===== Danger zone ===== */}
        <div className="settingsCard danger">
          <h2 className="settingsCardTitle">Danger zone</h2>
          <div className="settingsRow between">
            <span className="settingsValue">Sign out of your account</span>
            <button className="setPillBtn ghost" onClick={handleLogout}>
              <FiLogOut size={14} /> Log out
            </button>
          </div>
          <div className="settingsRow between">
            <span className="settingsValue">Permanently delete your account and all data</span>
            {!confirmDelete ? (
              <button className="setPillBtn dangerGhost" onClick={() => setConfirmDelete(true)}>
                <FiTrash2 size={14} /> Delete account
              </button>
            ) : (
              <div className="confirmDeleteRow">
                <button className="setPillBtn ghost" onClick={() => setConfirmDelete(false)}>Cancel</button>
                <button className="setPillBtn dangerSolid" onClick={handleDeleteAccount} disabled={deleting}>
                  {deleting ? 'Deleting…' : 'Confirm delete'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
